<template>
  <div :class="{'content-wrap': !isMobile, fixed: isOpenItems }">
    <fsf-htl-switch v-if="isMobile" />
    <glass
      :class="{ active: destin.typeahead.open || origin.typeahead.open, 'content-wrap': isMobile }"
      class="fsf-glass"
    >
      <h1
        v-if="isMobile && !isABTestNewVersion"
        v-dompurify-html="banners.title"
        class="form-title"
      />
      <fsf-htl-switch v-else-if="!isMobile" />

      <template v-if="rip">
        <rip-block :texts="ripTexts" />
      </template>
      <template v-else>
        <flight-search v-if="isABTestNewVersion"/>
        <flight-search-form v-else />
      </template>
    </glass>

    <glass
      v-if="!isABTestNewVersion && showHistogramBlock"
      class="histogram-glass"
    >
      <section class="histogram-wrapper">
        <div class="histogram-header">
          <h2 id="histogram-title">{{ $t('searchForm.histogram.indexTitle') }}</h2>
          <close-icon @click="closeHistogram" />
        </div>

        <div
          v-dompurify-html="$t('searchForm.histogram.disclaimer')"
          class="histogram-descr"
        />
        <div class="histogram-container">
          <histogram
            :prices-from-parent="histogramPrices"
            :destination-code="destin.value.code"
            :origin-code="origin.value.code"
            :promo="promoCodes"
            :trip-type="tripType"
            :num-adt="paxSelector.adults"
            :num-chd="paxSelector.children"
            :num-inf="paxSelector.infants"
            :sref="'FSH'"
            :show-navigation="isDesktop"
            :disable-tooltip="true"
            :show-legend="true"
            :column-max-height-in-pixels="getHistogramSize"
            :is-group-booking="paxSelector.groupBooking"
            :group-booking-url="getGroupBookingUrl"
            :full-scroll="true"
            @promoError="showPromoCodeBlock"
            @histogramSearch="histogramSearch"
          />
        </div>
      </section>
    </glass>
  </div>
</template>

<script>
  import get from 'lodash-es/get'
  import isUndefined from 'lodash-es/isUndefined'
  import PromoCodesMixin from 'components/eHorizon/SearchFormMixins/PromoCodes'
  import RecentSearchMixin from 'components/eHorizon/SearchFormMixins/RecentSearch'
  import SearchFormMixin from 'components/eHorizon/SearchFormMixins/SearchForm'
  import Focusout from 'utils/directives/FocusOut'
  import Glass from 'components/Glass'
  import HistogramService from 'services/AirBaltic/Destinations/Histogram'
  import { mapGetters, mapState } from 'vuex'
  import gtmFSF from 'services/AirBaltic/GTM/FSF'
  import FsfHtlSwitch from './FsfHtlSwitch.vue'
  import ABTestUtils from 'utils/ABTestUtils'

  export default {
    name: 'FLT',
    directives: {
      Focusout
    },
    components: {
      FsfHtlSwitch,
      Glass,
      Histogram: () =>
        import(/* webpackChunkName: "histogram-graph" */ 'components/Histogram/Histogram'),
      RipBlock: () =>
        import(/* webpackChunkName: "rip-block" */ 'components/Forms/Index/RipBlock'),
      FlightSearchForm: () => import(/* webpackChunkName: "flight-search-form"*/ 'components/Forms/Index/Form/FlightSearchForm'),
      FlightSearch: () => import(/* webpackChunkName: "f-s-form"*/ 'components/FlightSearchForm/SearchForm/FlightSearchForm.vue'),
      'close-icon': () =>
        import(/* webpackChunkName: "close-icon"*/ 'components/CloseIcon')
    },
    mixins: [PromoCodesMixin, SearchFormMixin, RecentSearchMixin],
    props: {
      rip: {
        type: Boolean,
        required: false,
        default: false
      },
      ripTexts: {
        type: Object,
        required: false,
        default: () => ({})
      },
      banners: {
        type: Object,
        required: true,
        default: () => ({
          title: '',
          description: ''
        })
      }
    },
    data() {
      return {
        histogramPrices: []
      }
    },
    computed: {
      ...mapState('eHorizonSearchForm', [
        'paxSelector',
        'tripType',
        'destin',
        'origin',
        'promoCodes',
        'minimized',
        'directFlights',
        'directFlightsRoundTrip',
        'recentSearch',
        'sref',
        'showHistogram'
      ]),
      ...mapGetters('device', ['isMobile', 'isTablet', 'isDesktop']),
      origDestin() {
        return {
          origin: get(this.origin, 'value.code', null),
          destin: get(this.destin, 'value.code', null)
        }
      },
      isABTestNewVersion() {
        return ABTestUtils.isNewVersionEnabled()
      },
      getHistogramSize() {
        if (this.isMobile) {
          return 55
        } else if (this.isTablet) {
          return 80
        }

        return 120
      },
      showHistogramBlock() {
        return this.showHistogram && !this.isOpenItems
      },
      isOpenItems() {
        if (this.isMobile) {
          return this.paxSelector.dropdown.open || this.promoCodes.dropdown.open
        }

        return false
      }
    },

    mounted() {
      document.body.setAttribute('tabindex', -1)
    },

    methods: {
      closeHistogram() {
        this.$store.commit('eHorizonSearchForm/setShowHistogram', false)
      },
      closeOverlay() {
        if (this.isOverlayVisible()) {
          if (this.paxSelector.dropdown.open) {
            this.paxSelectorClose()
          }
          if (this.promoCodes.dropdown.open) {
            this.closePromoModal()
          }
          if (this.origin.typeahead.open) {
            this.originCloseAll()
          }
          if (this.destin.typeahead.open) {
            this.destinCloseAll()
          }
        }
      },
      isOverlayVisible() {
        return (
          this.isTablet &&
          (this.paxSelector.dropdown.open ||
            this.promoCodes.dropdown.open ||
            this.origin.typeahead.open ||
            this.destin.typeahead.open)
        )
      },
      histogramSearch(month) {
        const tripType = this.tripType === 'return' ? 2 : 1
        const searchObj = {
          fromCode: this.origin.value.code,
          fromType: this.origin.value.type,
          toCode: this.destin.value.code,
          toType: this.destin.value.type,
          tripType,
          adults: this.paxSelector.adults,
          children: this.paxSelector.children,
          infants: this.paxSelector.infants,
          departure: '',
          return: '',
          promoCodes: this.promoCodes.promoCodes,
          price: ''
        }
        this.addSearchToCookie(searchObj)
        gtmFSF.flightSearch('flight_search_calendar', searchObj)
      },
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .content-wrap {
    padding: 12px 16px 40px 16px;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: white;

    &.fixed {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 4;
      padding: 0 0 28px;

      .glass-wrapper {
        height: 100%;
      }
    }

    @include bp-mf-tablet {
      padding: 16px 24px 32px;
      height: auto;
    }

    @include bp-mf-desktop {
      margin: 0 auto;
      padding: 16px 12px 32px;
      flex-direction: row;
      justify-content: space-between;
      background: transparent;
      width: 100%;

      .glass-wrapper {
        width: auto;
        min-width: calc(50% - 10px);

        &.active {
          background: rgba(white, 0.64);
          padding: 28px 24px 32px;
          border-radius: 6px;
          width: auto;
        }
      }
    }

    @include bp-mf-desktop-large {
      padding: 16px 0 32px;
    }
  }

  .glass-wrapper {
    min-height: 336px;

    @include bp-mf-desktop {
      min-height: 310px;
    }
  }

  .histogram-glass {
    z-index: 0;
    min-height: auto;

    @include bp-mf-desktop {
      z-index: 1;
    }
  }

  .form-title {
    @include defaultFontValues($blue-dark, 24, 28, 500);
    margin: 0 0 16px;

    @include bp-mf-desktop {
      font-size: em(32);
      margin: 0 0 29px;
    }
  }

  ::v-deep .histogram-container {
    margin-top: 10px;

    @include bp-mf-desktop {
      background: white;
      padding: 20px 0 20px 23px;
      display: flex;
      flex-direction: column;
      margin: 0;
      border-radius: 6px;
    }
  }

  ::v-deep .histogram {
    .histogram-content {
      @include bp-mf-desktop {
        overflow-x: hidden !important;
        overflow-y: hidden !important;
        margin-right: 16px;
      }
    }

    .histogram-wrap {
      padding-right: 12px;

      &:last-of-type {
        @include bp-mf-tablet {
          padding-right: 0 !important;
        }
      }
    }
  }

  ::v-deep .histogram-legend {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    order: 0;
    margin: 24px 0 12px !important;
    width: auto !important;

    @include bp-mf-desktop {
      margin: 0 0 20px !important;
      order: -1 !important;
    }

    .legend {
      width: calc(50% - 6px);
      margin-bottom: 0;
    }
  }

  ::v-deep .histogram-wrapper {
    padding-top: 17px;

    h2 {
      @include defaultFontValues($blue-dark, 14, 22, 500);
      margin: 0 0 5px;

      @include bp-mf-tablet {
        font-size: em(16);
        line-height: 28px;
        margin: 0 0 7px;
      }

      @include bp-mf-desktop {
        font-size: em(20);
        line-height: 22px;
        margin-bottom: 8px;
      }
    }

    .histogram-descr {
      color: $blue-secondary;
      font-size: em(12);
      line-height: 18px;
      margin-bottom: 23px;

      @include bp-mf-desktop {
        font-size: em(14);
        color: $blue-dark;
      }
    }

    @include bp-mf-desktop {
      padding-top: 0;
    }
  }

  ::v-deep .glass-wrapper {
    &.active {
      @include bp-mf-desktop {
        background: rgba(white, 0.64);
      }

      .input-container.visible {
        @include bp-mf-tablet {
          top: 0;
          left: 0;
        }
      }

      .e-horizon-typeahead {
        .dropdown-menu {
          @include bp-tablet-only {
            box-shadow: none;
          }

          &.visible {
            @include bp-mf-tablet {
              top: 0;
              left: 0;
              width: calc(100% - 56px);
            }
          }

          .dropdown-content {
            @include bp-tablet-only {
              margin-left: 1px;
              box-shadow: -1px 0 0 0 rgba(black, 0.1), 0 2px 10px 0 rgba(black, 0.16);
            }

            @include bp-mf-desktop {
              width: 100%;
            }
          }

          .e-horizon-modal-header {
            @include bp-tablet-only {
              border-left: 1px solid $grey-border-light;
            }
          }
        }
      }

      .swap-icon {
        z-index: 3;

        @include bp-mf-tablet {
          z-index: 4;
        }
      }
    }

    .e-horizon-modal-header {
      @include bp-mf-tablet {
        height: 56px;
      }

      .close-icon {
        @include bp-mf-tablet {
          display: none;
        }
      }
    }

    .passengers-block {
      margin-left: 12px;
    }
  }

  .histogram-header {
    display: flex;
    justify-content: space-between;
  }

  .histogram-wrapper {
    ::v-deep .close-icon {
      position: relative;
      color: $blue-dark;
    }
  }
</style>
